import { PartialNullable } from "../../Extensions";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32 } from "../../RadObject";
import { IPanel, Panel, PanelPropertyValues } from "./Panel";
import { NAMESPACE } from "./_Namespace";

import "./InfiniteScrollPanel.scss";

export type InfiniteScrollPanelPropertyValues =
    PanelPropertyValues
    & {
        hasNextResults: boolean;
        hasPreviousResults: boolean;
        scrollTop: number;
    };

export type InfiniteScrollPanelProperties = {
    [name in keyof InfiniteScrollPanelPropertyValues]: Property<InfiniteScrollPanelPropertyValues[name]>
};

export interface IInfiniteScrollPanel extends IPanel, InfiniteScrollPanelProperties
{
}

export class InfiniteScrollPanel extends Panel implements IInfiniteScrollPanel
{
    // #region Static

    public static override classNamespace = NAMESPACE + "InfiniteScrollPanel";
    public static override className = "InfiniteScrollPanel";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<InfiniteScrollPanelPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                hasNextResults: true,
                hasPreviousResults: true,
                scrollTop: 0,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$bottomLoader?: JNative<HTMLElement>;
    protected $$topLoader?: JNative<HTMLElement>;

    // #endregion

    // #region Properties

    public hasNextResults!: PropertyBoolean;
    public hasPreviousResults!: PropertyBoolean;
    public scrollTop!: PropertyInt32;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<InfiniteScrollPanelPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-infinite-scroll-panel");

        // Trigger Property Changes
        this.hasNextResults.triggerChange();
        this.hasPreviousResults.triggerChange();
        this.scrollTop.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public isContentScrollable()
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return false;

        if (!this._containerElementRef.current)
            return false;

        return this._containerElementRef.current[0].clientHeight > $$element[0].clientHeight;
    }

    // #endregion

    // #region Protected Methods

    protected _hasNextResultsChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.removeCssClass("rad-state-no-next-results");
        else
            this.addCssClass("rad-state-no-next-results");
    }
    protected _hasPreviousResultsChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.removeCssClass("rad-state-no-previous-results");
        else
            this.addCssClass("rad-state-no-previous-results");
    }
    protected _restoreScrollTop()
    {
        if (!this._isShown())
            return;

        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        const
            scrollTop = this.scrollTop.get();

        if (scrollTop != null)
            $$element.scrollTop(scrollTop);
    }
    protected _scrollTopChanged(change: PropertyChange<number>)
    {
        const
            $$element = this.$$element.get(),
            scrollTop = change.currentValue ?? 0,
            elementScrollTop = Math.round($$element?.scrollTop() ?? 0);

        if (elementScrollTop !== scrollTop)
            $$element?.scrollTop(scrollTop);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: InfiniteScrollPanelPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.hasNextResults = new PropertyBoolean(this, propertyValues.hasNextResults, this._hasNextResultsChanged);
        this.hasPreviousResults = new PropertyBoolean(this, propertyValues.hasPreviousResults, this._hasPreviousResultsChanged);
        this.scrollTop = new PropertyInt32(this, propertyValues.scrollTop, this._scrollTopChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.on("scroll", { self: this }, this.onScroll);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.$$topLoader =
            JNativeStatic.create$$({
                nodeName: "div",
                attributes: { "class": "rad-loader rad-loader-top" },
                children: [
                    { nodeName: "div", attributes: { "class": "ui active centered inline loader" } }
                ]
            }).appendTo($$element);

        const
            $$containerElement = JNativeStatic.create$$({ nodeName: "div" }).appendTo($$element);

        this.$$bottomLoader =
            JNativeStatic.create$$({
                nodeName: "div",
                attributes: { "class": "rad-loader rad-loader-bottom" },
                children: [
                    { nodeName: "div", attributes: { "class": "ui active centered inline loader" } }
                ]
            }).appendTo($$element);

        this._containerElementRef.current = $$containerElement;

        return $$element;
    }

    // #endregion

    // #region Event Handlers

    protected override onAncestorVisibilityChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (change.currentValue)
            self._restoreScrollTop();
    }
    protected onScroll(evt: IJNativeEvent)
    {
        if (!(evt.target instanceof HTMLElement))
            return;

        const
            self = evt.data.self as this,
            $$element = $$(evt.target),
            scrollTop = Math.round($$element.scrollTop() ?? 0);

        let
            location: string | undefined;

        self.scrollTop.set(scrollTop);

        if (scrollTop === 0 && self.hasPreviousResults.get())
            location = "top";
        else if ($$element[0].scrollHeight - $$element[0].scrollTop - $$element[0].clientHeight < 5 && self.hasNextResults.get())
            location = "bottom";

        if (location)
        {
            const
                scrollHeight = Math.round($$element[0].scrollHeight);

            self.triggerHandler(
                "loadMore",
                {
                    location: location,
                    callback: function ()
                    {
                        if (location === "top")
                            self.scrollTop.set(Math.round($$element[0].scrollHeight) - scrollHeight);
                    }
                }
            );
        }
    }

    // #endregion

    // #endregion
}